import { Template, Slide } from "types/ReportingDashboard/reporting.types"
import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"
import { createTemplateId, createElementId } from "utils/idGenerators"

/**
 * Utility functions for template operations
 */
export const templateOperations = {
  /**
   * Migrates template elements to slide elements by removing template flags and generating new IDs
   */
  migrateTemplateElements: (templateElements: SlideElement[]): SlideElement[] =>
    templateElements.map((el: SlideElement) => ({
      ...el,
      id: createElementId(el.type),
      isTemplateElement: false,
    })),

  /**
   * Creates a new template object with the provided data
   */
  createNewTemplate: (
    name: string,
    elements: SlideElement[],
    tableConfigs: Record<string, DataElementConfig>,
    backgroundColor: string
  ): Template => ({
    id: createTemplateId(),
    name,
    elements: elements.map((el) => ({ ...el, isTemplateElement: true })),
    tableConfigs,
    backgroundColor,
    timestamp: Date.now(),
  }),

  /**
   * Gets the effective background color for a slide considering template inheritance
   */
  getTemplateBackgroundColor: (
    slide: Slide,
    templates: Record<string, Template>,
    defaultColor: string
  ): string => {
    return (
      slide.backgroundColor ||
      (slide.templateId
        ? templates[slide.templateId]?.backgroundColor
        : null) ||
      defaultColor
    )
  },

  /**
   * Removes template from slide and migrates template elements to slide elements
   */
  removeTemplateFromSlide: (
    slide: Slide,
    templates: Record<string, Template>
  ): Slide => {
    const oldTemplate = slide.templateId ? templates[slide.templateId] : null
    const migratedElements = oldTemplate
      ? templateOperations.migrateTemplateElements(oldTemplate.elements || [])
      : []

    return {
      ...slide,
      templateId: null,
      slideElements: [...migratedElements, ...(slide.slideElements || [])],
      slideTableConfigs: {
        ...(oldTemplate?.tableConfigs || {}),
        ...(slide.slideTableConfigs || {}),
      },
      backgroundColor:
        slide.backgroundColor || oldTemplate?.backgroundColor || "#ffffff",
    }
  },

  /**
   * Applies template to slide by clearing slide-specific content
   */
  applyTemplateToSlide: (slide: Slide, templateId: string): Slide => ({
    ...slide,
    templateId,
    slideElements: [],
    slideTableConfigs: {},
    backgroundColor: undefined,
  }),

  /**
   * Updates slide with template (either applies or removes)
   */
  updateSlideWithTemplate: (
    slide: Slide,
    templateId: string | null,
    templates: Record<string, Template>
  ): Slide => {
    if (templateId === null) {
      return templateOperations.removeTemplateFromSlide(slide, templates)
    } else {
      return templateOperations.applyTemplateToSlide(slide, templateId)
    }
  },

  /**
   * Creates template modal data for creating a new template
   */
  createTemplateModalData: (
    name: string
  ): { action: "create"; name: string } => ({
    action: "create",
    name,
  }),

  /**
   * Creates template modal data for replacing an existing template
   */
  replaceTemplateModalData: (
    templateId: string
  ): { action: "replace"; name: string } => ({
    action: "replace",
    name: templateId,
  }),
}

/**
 * State management utilities for template operations
 */
export const templateStateUtils = {
  /**
   * Resets all template-related modal and editing state
   */
  resetTemplateModalState: (setters: {
    setIsEditingTemplate: (value: boolean) => void
    setEditingTemplate: (value: string | null) => void
    setTemplateModalVisible: (value: boolean) => void
    setSaveSourceIsTemplate: (value: boolean) => void
    setOriginalTemplateState: (value: Template | null) => void
  }) => {
    setters.setIsEditingTemplate(false)
    setters.setEditingTemplate(null)
    setters.setTemplateModalVisible(false)
    setters.setSaveSourceIsTemplate(false)
    setters.setOriginalTemplateState(null)
  },

  /**
   * Sets up state for editing an existing template
   */
  setupTemplateEditing: (
    templateKey: string,
    templates: Record<string, Template>,
    setters: {
      setOriginalTemplateState: (value: Template | null) => void
      setTemplateModalMode: (value: "create" | "edit") => void
      setEditingTemplate: (value: string) => void
      setIsEditingTemplate: (value: boolean) => void
    }
  ) => {
    const template = templates[templateKey]
    setters.setOriginalTemplateState(
      template ? JSON.parse(JSON.stringify(template)) : null
    )
    setters.setTemplateModalMode("edit")
    setters.setEditingTemplate(templateKey)
    setters.setIsEditingTemplate(true)
  },

  /**
   * Sets up state for creating a new template
   */
  setupTemplateCreation: (
    isEditingTemplate: boolean,
    editingTemplate: string | null,
    setters: {
      setSaveSourceIsTemplate: (value: boolean) => void
      setTemplateModalMode: (value: "create" | "edit") => void
      setEditingTemplate: (value: string | null) => void
      setTemplateModalVisible: (value: boolean) => void
    }
  ) => {
    if (isEditingTemplate && editingTemplate) {
      setters.setSaveSourceIsTemplate(true)
      setters.setTemplateModalMode("edit")
    } else {
      setters.setSaveSourceIsTemplate(false)
      setters.setTemplateModalMode("create")
      setters.setEditingTemplate(null)
    }
    setters.setTemplateModalVisible(true)
  },
}
